import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import { db, type NoteQueued } from "@/db";
import { useNoteSyncStore } from "@/offline/noteSyncStore";
import { formatTimestamp, statusDisplay } from "@/offline/noteUtils";
import { useNavigate } from "react-router-dom";

interface NoteWithAddress {
  note: NoteQueued;
  addressLine: string;
}

export default function SyncManager() {
  const [pending, setPending] = useState<NoteWithAddress[]>([]);
  const [failed, setFailed] = useState<NoteWithAddress[]>([]);
  const [loading, setLoading] = useState(false);

  const syncing = useNoteSyncStore((s) => s.syncing);
  const syncNow = useNoteSyncStore((s) => s.syncNow);
  const refreshCounts = useNoteSyncStore((s) => s.refreshCounts);
  const pendingCountStore = useNoteSyncStore((s) => s.pendingCount);
  const failedCountStore = useNoteSyncStore((s) => s.failedCount);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const [pendingNotes, failedNotes] = await Promise.all([
      db.notesQueue.where("queue_status").equals("pending").toArray(),
      db.notesQueue.where("queue_status").equals("failed").toArray(),
    ]);

    const udprns = Array.from(
      new Set([...pendingNotes, ...failedNotes].map((n) => n.udprn))
    );
    const addresses = udprns.length
      ? await db.addresses.where("udprn").anyOf(udprns).toArray()
      : [];
    const addressMap = new Map<number, string>();
    addresses.forEach((a) => {
      const line =
        a.toDisplay ??
        (a as any).to_display ??
        a.postcode_normalised ??
        `UDPRN ${a.udprn}`;
      addressMap.set(a.udprn, line);
    });

    const toWithAddr = (arr: NoteQueued[]) =>
      arr
        .sort((a, b) =>
          String(a.created_at_client ?? "").localeCompare(String(b.created_at_client ?? ""))
        )
        .map((note) => ({
          note,
          addressLine: addressMap.get(note.udprn) ?? `UDPRN ${note.udprn}`,
        }));

    setPending(toWithAddr(pendingNotes));
    setFailed(toWithAddr(failedNotes));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    load();
  }, [pendingCountStore, failedCountStore, load]);

  const total = useMemo(() => pending.length + failed.length, [pending, failed]);

  const handleRetry = async (note: NoteQueued) => {
    if (!note.id) return;
    await db.notesQueue.update(note.id, {
      queue_status: "pending",
      error: null,
      last_attempt_at: null,
      retry_count: 0,
    });
    await load();
    await refreshCounts();
    await syncNow({ includeFailed: true });
  };

  const handleDelete = async (note: NoteQueued) => {
    if (!note.id) return;
    await db.notesQueue.delete(note.id);
    await load();
    await refreshCounts();
  };

  const section = (
    title: string,
    items: NoteWithAddress[],
    emptyText: string,
    failed?: boolean
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {title} {items.length ? `(${items.length})` : ""}
      </Typography>
      {!items.length ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        <List sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          {items.map(({ note, addressLine }) => {
            const payloadBody = note.payload?.body ?? note.payload ?? {};
            return (
              <ListItem
                key={note.id ?? note.local_id}
                alignItems="flex-start"
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    {failed && (
                      <Button
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={() => handleRetry(note)}
                      >
                        Retry
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(note)}
                    >
                      Delete
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primaryTypographyProps={{ component: "div" }}
                  secondaryTypographyProps={{ component: "div" }}
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={700}>
                        {addressLine}
                      </Typography>
                      <Chip
                        size="small"
                        label={statusDisplay(payloadBody.status, payloadBody.status_display)}
                      />
                      {failed ? (
                        <Chip size="small" color="error" label="Failed" />
                      ) : (
                        <Chip size="small" color="warning" label="Pending" />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Stack spacing={0.5}>
                      {payloadBody.note ? (
                        <Typography variant="body2" color="text.secondary">
                          {payloadBody.note}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatTimestamp(payloadBody.client_created_at ?? note.created_at_client)}
                      </Typography>
                      {note.last_attempt_at && (
                        <Typography variant="caption" color="text.secondary">
                          Last attempt: {formatTimestamp(note.last_attempt_at)} (retries:{" "}
                          {note.retry_count ?? 0})
                        </Typography>
                      )}
                      {note.error && (
                        <Typography variant="caption" color="error">
                          {note.error}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        onClick={() => navigate(`/address/${note.udprn}`)}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        View address
                      </Button>
                    </Stack>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              Status
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={async () => {
                await syncNow({ includeFailed: true });
                await load();
              }}
              disabled={syncing}
            >
              Sync now
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {loading
              ? "Loading…"
              : total === 0
              ? "All notes synced"
              : `${total} note${total === 1 ? "" : "s"} need syncing`}
          </Typography>
        </CardContent>
      </Card>

      {section("Pending notes", pending, "No pending notes.")}

      <Divider sx={{ my: 2 }} />

      {failed.length > 0 && section("Failed notes", failed, "No failed notes.", true)}
    </Box>
  );
}
